import { PriceSummaryGroup, PriceSummaryLine } from '@voyagu/portal-design-system';
import {
  AncillaryTypeEnum,
  PortalAncillaryBundle,
  PortalHotelBooking,
  PortalOrderNode,
  PortalTierEnum,
} from '@app/services/api/api.types';
import { getActiveTierType, hasActiveTierAncillary } from '@app/pages/tiers-page/utils';
import { getBasicChangeFeeTotal } from '@app/utils/basic-change-fee';
import { fromISO8601 } from '@app/utils/date';
import { getFormatedCurrency } from '@app/utils/utils';

const tierPlanNames: Record<PortalTierEnum, string> = {
  [PortalTierEnum.ESSENTIAL]: 'Essential Plan',
  [PortalTierEnum.VIP]: 'VIP Plan',
  [PortalTierEnum.EXECUTIVE]: 'Executive Plan',
};

export type PriceSummaryInput = {
  option: PortalOrderNode;
  tier?: PortalTierEnum;
  bundle?: PortalAncillaryBundle;
  disruptionProtection?: number;
  tipsAmount?: number;
  cancelForAnyReason?: number;
  promocodeDiscountAmount?: number;
};

export function buildPriceSummary(input: PriceSummaryInput) {
  const { option } = input;
  const tierPricing = getTierPricing(option, input.tier);
  const groups: PriceSummaryGroup[] = [];

  if (option.cashback?.eligible) {
    groups.push({
      id: 'cashback',
      icon: 'walletBifold',
      title: 'Cashback',
      amount: money(option.cashback.amount),
      amountTone: 'success',
    });
  }

  if (option.flightsTotal || option.isChange) {
    const lines = fareLines(option);

    groups.push({
      id: 'flight',
      icon: 'flightTakeoff',
      title: `Flight${option.isChange ? ' price difference' : ''}`,
      amount: money(option.flightsTotal),
      lines,
      collapsible: lines.length > 0,
    });
  }

  const basicChangeFeeTotal = getBasicChangeFeeTotal(option);

  if (option.isChange && basicChangeFeeTotal > 0) {
    const lines = passengerUnitLines(option, option.changeFeePerPax);

    groups.push({
      id: 'change-fee',
      icon: 'refresh',
      title: 'Change fee',
      amount: money(basicChangeFeeTotal),
      lines,
      collapsible: lines.length > 0,
      collapsed: true,
    });
  }

  const changeFeePlanName = planName(getActiveTierType(option));

  if (option.isChange && hasActiveTierAncillary(option) && changeFeePlanName) {
    groups.push(coveredChangeFeeGroup(option, changeFeePlanName));
  }

  if (tierPricing.tierPrice) {
    const lines =
      option.isChange && tierPricing.tierAmountPerTraveler && option.numberOfAdults
        ? [unitLine(tierPricing.tierAmountPerTraveler, option.numberOfAdults, 'adult', 'adults')]
        : [];

    groups.push({
      id: 'tier',
      icon: 'person',
      title: tierPlanTitle(option, input.tier),
      amount: money(tierPricing.tierPrice),
      lines,
      collapsible: lines.length > 0,
    });
  }

  const hotelBookings = option.hotelBookings || [];

  if (hotelBookings.length) {
    groups.push({
      id: 'stay',
      icon: 'hotel',
      title: 'Stay',
      amount: 'Included',
      amountTone: 'success',
      lines: hotelBookings.map((booking) => stayLine(booking)),
      collapsible: true,
    });
  }

  if (input.bundle) {
    const lines = input.bundle.ancillaries.map((ancillary) => ({
      label:
        ancillary.type === AncillaryTypeEnum.CANCEL_FOR_ANY_REASON
          ? 'Cancel for any reason'
          : 'Premium Disruption Assistance',
      amount: money(ancillary.price),
    }));

    groups.push({
      id: 'bundle',
      title: 'Full Peace Bundle',
      amount: money(input.bundle.price),
      amountTone: 'success',
      lines,
      collapsible: lines.length > 0,
      collapsed: true,
    });
  }

  if (input.disruptionProtection) {
    const lines = splitAcrossTravelers(option, input.disruptionProtection);

    groups.push({
      id: 'disruption',
      icon: 'supportAgent',
      title: 'Premium disruption assistance',
      amount: money(input.disruptionProtection),
      lines,
      collapsible: lines.length > 0,
    });
  }

  if (input.cancelForAnyReason) {
    const lines = splitAcrossTravelers(option, input.cancelForAnyReason);

    groups.push({
      id: 'cancel-for-any-reason',
      icon: 'xmarkShield',
      title: 'Cancel for any reason',
      amount: money(input.cancelForAnyReason),
      lines,
      collapsible: lines.length > 0,
    });
  }

  if (input.tipsAmount) {
    groups.push({
      id: 'gratuity',
      icon: 'dollarsign',
      title: 'Gratuity',
      amount: money(input.tipsAmount),
    });
  }

  if (input.promocodeDiscountAmount) {
    groups.push({
      id: 'promo',
      icon: 'payments',
      title: 'Promo code',
      amount: money(-input.promocodeDiscountAmount),
      amountTone: 'discount',
    });
  }

  return {
    total: money(priceTotal(input, tierPricing.tierPrice, basicChangeFeeTotal)),
    groups,
  };
}

function priceTotal(input: PriceSummaryInput, tierPrice: number, basicChangeFeeTotal: number) {
  return (
    tierPrice +
    (input.option.flightsTotal || 0) +
    (input.disruptionProtection || 0) +
    (input.tipsAmount || 0) +
    (input.cancelForAnyReason || 0) +
    (input.bundle?.price || 0) +
    basicChangeFeeTotal -
    (input.promocodeDiscountAmount || 0)
  );
}

function getTierPricing(option: PortalOrderNode, tier?: PortalTierEnum) {
  if (!tier) {
    return { tierPrice: 0, tierAmountPerTraveler: 0 };
  }

  const ancillary = option.ancillaries?.find((item) => item.type === (tier as unknown));

  if (!ancillary) {
    return { tierPrice: 0, tierAmountPerTraveler: 0 };
  }

  return {
    tierPrice: Number(ancillary.price),
    tierAmountPerTraveler: Number(ancillary.amountPerTraveler),
  };
}

function tierPlanTitle(option: PortalOrderNode, tier?: PortalTierEnum) {
  if (tier) {
    return planName(tier) || `${tier} Plan`;
  }

  return planName(getActiveTierType(option));
}

function planName(tier?: PortalTierEnum) {
  return tier ? tierPlanNames[tier] : '';
}

function coveredChangeFeeGroup(
  option: PortalOrderNode,
  changeFeePlanName: string,
): PriceSummaryGroup {
  const voluntaryChangeFeeMax = option.voluntaryChangeFeeMax ?? 0;
  const adults = option.numberOfAdults || 0;
  const grossTotal = voluntaryChangeFeeMax * adults;
  const lines: PriceSummaryLine[] = [];

  if (voluntaryChangeFeeMax && adults) {
    lines.push(unitLine(voluntaryChangeFeeMax, adults, 'adult', 'adults'));
  }

  lines.push({
    label: `Covered by ${changeFeePlanName}`,
    amount: money(-grossTotal),
    tone: 'discount',
  });

  return {
    id: 'covered-change-fee',
    icon: 'checkmark',
    title: `Change fee · ${changeFeePlanName}`,
    amount: money(0),
    amountTone: 'success',
    lines,
    collapsible: true,
    collapsed: true,
  };
}

function fareLines(option: PortalOrderNode) {
  const lines: PriceSummaryLine[] = [];

  if (option.soldAmountPerAdult && option.numberOfAdults) {
    lines.push(unitLine(option.soldAmountPerAdult, option.numberOfAdults, 'adult', 'adults'));
  }

  if (option.soldAmountPerChild && option.numberOfChildren) {
    lines.push(unitLine(option.soldAmountPerChild, option.numberOfChildren, 'child', 'children'));
  }

  if (option.soldAmountPerInfant && option.numberOfInfants) {
    lines.push(unitLine(option.soldAmountPerInfant, option.numberOfInfants, 'infant', 'infants'));
  }

  return lines;
}

function splitAcrossTravelers(option: PortalOrderNode, total: number) {
  const travelerCount = (option.numberOfAdults || 0) + (option.numberOfChildren || 0);

  if (!travelerCount) {
    return [];
  }

  return passengerUnitLines(option, total / travelerCount);
}

function passengerUnitLines(option: PortalOrderNode, unitPrice: number | null | undefined) {
  if (!unitPrice) {
    return [];
  }

  const lines: PriceSummaryLine[] = [];
  const adults = option.numberOfAdults || 0;
  const children = option.numberOfChildren || 0;

  if (adults) {
    lines.push(unitLine(unitPrice, adults, 'adult', 'adults'));
  }

  if (children) {
    lines.push(unitLine(unitPrice, children, 'child', 'children'));
  }

  return lines;
}

function unitLine(
  unitPrice: number,
  count: number,
  singular: string,
  plural: string,
): PriceSummaryLine {
  return {
    label: `${money(unitPrice)} × ${count} ${count === 1 ? singular : plural}`,
    amount: money(unitPrice * count),
  };
}

function stayLine(booking: PortalHotelBooking): PriceSummaryLine {
  const nights = stayNights(booking.checkInDate, booking.checkoutDate);

  return {
    label: `${nights} ${nights === 1 ? 'night' : 'nights'} in ${booking.city}`,
    amount: 'Included',
    tone: 'success',
  };
}

function stayNights(checkInDate: string, checkOutDate: string) {
  const checkIn = fromISO8601(checkInDate);
  const checkOut = fromISO8601(checkOutDate);

  return (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24);
}

function money(value: number) {
  return getFormatedCurrency(Number(value) || 0);
}
